/* MASTONER_XCLOUD_GLOBAL_SEARCH_20260825_START */
/* global Element, HTMLElement, MutationObserver, NodeFilter, document, window */

(() => {
  "use strict";

  const script = document.currentScript;
  const configuredRoutes = new Set(
    (script?.dataset.mastonerXcloudGlobalSearchRoutes || "/a/search")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean),
  );
  const pathname = window.location.pathname;
  if (!configuredRoutes.has(pathname) || !["/a/search", "/search"].includes(pathname)) return;

  const API_NAME = "__mastonerXcloudGlobalSearch20260825";

  if (pathname === "/search") {
    const nativeParameters = new window.URLSearchParams(window.location.search);
    const nativeQueries = nativeParameters.getAll("q");
    const nativeTypes = nativeParameters.getAll("type");
    const nativeTerms = String(nativeQueries[0] || "").trim();
    if (
      nativeQueries.length === 1 &&
      nativeTypes.length === 1 &&
      nativeTypes[0] === "product" &&
      nativeTerms !== ""
    ) {
      const xcloudParameters = new window.URLSearchParams();
      xcloudParameters.set("q", nativeTerms);
      xcloudParameters.set("type", "product");
      window.location.replace(`/a/search?${xcloudParameters.toString()}`);
    }
    return;
  }

  const rootSelector =
    script?.dataset.mastonerXcloudGlobalSearchRoot ||
    "#cloud_search_filters_root, .cloud-search-page-controls";
  const labels = new Map([
    ["search", "Buscar en estos resultados"],
    ["collection", "Categoría"],
    ["fabricante / marca impresora", "Fabricante / marca impresora"],
    ["original / compatible", "Original / compatible"],
    ["tipo de consumible", "Tipo de consumible"],
    ["color", "Color"],
    ["capacidad", "Capacidad"],
    ["rendimiento", "Rendimiento"],
    ["marca", "Marca"],
    ["availability", "Disponibilidad"],
    ["price", "Precio"],
  ]);
  const filterAliases = new Map(
    [
      ["search", "search"],
      ["collection", "collection"],
      ["category", "collection"],
      ["categoria", "collection"],
      ["fabricante / marca impresora", "fabricante / marca impresora"],
      ["fabricante o marca impresora", "fabricante / marca impresora"],
      ["marca impresora", "fabricante / marca impresora"],
      ["printer brand", "fabricante / marca impresora"],
      ["original / compatible", "original / compatible"],
      ["original/compatible", "original / compatible"],
      ["original o compatible", "original / compatible"],
      ["tipo de consumible", "tipo de consumible"],
      ["consumable type", "tipo de consumible"],
      ["color", "color"],
      ["capacidad", "capacidad"],
      ["consumable capacity", "capacidad"],
      ["rendimiento", "rendimiento"],
      ["consumable yield", "rendimiento"],
      ["marca", "marca"],
      ["vendor", "marca"],
      ["availability", "availability"],
      ["disponibilidad", "availability"],
      ["price", "price"],
      ["precio", "price"],
    ].map(([alias, canonical]) => [normalize(alias), canonical]),
  );
  const filterOrder = new Map([
    ["search", 0],
    ["collection", 10],
    ["fabricante / marca impresora", 20],
    ["original / compatible", 30],
    ["tipo de consumible", 40],
    ["color", 50],
    ["capacidad", 60],
    ["rendimiento", 70],
    ["marca", 80],
    ["availability", 90],
    ["price", 100],
  ]);
  const primaryFilterAllowlist = new Set(["collection", "availability", "price"]);
  const mobileFilterAllowlist = new Set(["collection", "availability", "price"]);
  const contextualFilterDenylist = new Set(
    ["search", "product type", "discount", "sort by", "tags", "filter", "filters"].map(
      normalize,
    ),
  );
  const hiddenClass = "mastoner-xcloud-global-search-hidden";
  const allowedClass = "mastoner-xcloud-global-search-allowed";
  const allowedValueClass = "mastoner-xcloud-global-search-value-allowed";
  const mobileFiltersMedia = window.matchMedia("(max-width: 736px)");
  let scheduled = false;
  let activeTrigger = null;

  function compact(value) {
    return String(value || "")
      .replace(/\s+/gu, " ")
      .trim();
  }

  function normalize(value) {
    return compact(value)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/gu, "")
      .replace(/[-_]+/gu, " ")
      .toLowerCase();
  }

  function selectedCollectionValues(urlLike = window.location.href) {
    try {
      const url = urlLike instanceof window.URL
        ? urlLike
        : new window.URL(urlLike, window.location.href);
      return [
        ...new Set(
          [...url.searchParams.entries()]
            .filter(
              ([key]) => key === "filter_collection" || key.startsWith("filter_collection["),
            )
            .map(([, value]) => compact(value))
            .filter(Boolean),
        ),
      ];
    } catch {
      return [];
    }
  }

  function contextualFiltersEnabled() {
    return selectedCollectionValues().length > 0;
  }

  function xcloudRoot() {
    const candidate = document.querySelector(rootSelector);
    if (!(candidate instanceof Element)) return null;
    return (
      candidate.closest("#cloud_search_filters_root, .cloud-search-page-controls") || candidate
    );
  }

  function configureFilterPresentation(root) {
    const controls =
      root.querySelector(".cloud-search-page-controls") ||
      (root.querySelector(".cloud-search-page-controls__filters") ? root : null);
    if (!(controls instanceof HTMLElement)) return;

    const desktop = !mobileFiltersMedia.matches;
    controls.classList.toggle("cloud-search-filters-horizontal", false);
    controls.classList.toggle("cloud-search-filters-classic", true);
    controls.dataset.mastonerXcloudGlobalSearchPresentation = desktop ? "sidebar" : "drawer";
  }

  function ensureSearchLayout(root) {
    const section = document.querySelector("#shopify-section-main.cc-main-search");
    if (!(section instanceof HTMLElement) || !(root instanceof HTMLElement)) return false;
    if (!section.parentElement || root === section || root.contains(section)) return false;

    let layout = section.closest(".mastoner-xcloud-global-search-layout");
    const supportedMount =
      section.contains(root) ||
      root.parentElement === section.parentElement ||
      (layout instanceof HTMLElement && root.parentElement === layout.parentElement);
    if (!supportedMount) return false;

    if (!(layout instanceof HTMLElement)) {
      layout = document.createElement("div");
      layout.className = "mastoner-xcloud-global-search-layout";
      section.parentElement.insertBefore(layout, section);
    }
    // XCloud may insert controls inside #filter-results during initial load or refresh.
    // Move the same node so its filter state and event listeners are preserved.
    if (root.parentElement !== layout) layout.insertBefore(root, layout.firstChild);
    if (section.parentElement !== layout) layout.appendChild(section);

    const helper = document.querySelector("#shopify-section-mastoner-collection-helper");
    if (helper instanceof HTMLElement) {
      helper.classList.add(hiddenClass);
      helper.setAttribute("aria-hidden", "true");
    }
    return true;
  }

  function dockSortInToolbar(root) {
    const controls =
      root.querySelector(".cloud-search-page-controls") ||
      (root.matches?.(".cloud-search-page-controls") ? root : null);
    const filtersContainer = controls?.querySelector(
      ".cloud-search-page-controls__filters-container",
    );
    const toolbarHost = document.querySelector(
      "#shopify-section-main.cc-main-search #products-toolbar .products-toolbar__sort",
    );
    const candidates = [
      ...document.querySelectorAll(".cloud-search-page-controls__sort"),
    ];
    const sort =
      candidates.find((candidate) => root.contains(candidate)) ||
      candidates.find(
        (candidate) => candidate.dataset.mastonerXcloudGlobalSearchToolbarSort === "true",
      );
    if (!(sort instanceof HTMLElement) || !(filtersContainer instanceof HTMLElement)) return;

    if (mobileFiltersMedia.matches || !(toolbarHost instanceof HTMLElement)) {
      const panel = filtersContainer.querySelector(".cloud-search-page-controls__filters");
      if (sort.parentElement !== filtersContainer) filtersContainer.insertBefore(sort, panel || null);
      delete sort.dataset.mastonerXcloudGlobalSearchToolbarSort;
      return;
    }

    for (const duplicate of toolbarHost.querySelectorAll(".cloud-search-page-controls__sort")) {
      if (duplicate !== sort) duplicate.remove();
    }
    for (const nativeControl of [...toolbarHost.children]) {
      if (nativeControl === sort) continue;
      setDefensivelyHidden(nativeControl, true);
    }
    sort.dataset.mastonerXcloudGlobalSearchToolbarSort = "true";
    if (sort.parentElement !== toolbarHost) toolbarHost.appendChild(sort);
  }

  function dockDesktopFilterPanels(root) {
    if (mobileFiltersMedia.matches) return;
    for (const trigger of root.querySelectorAll(
      `.cloud-search-filter.${allowedClass}[aria-controls]`,
    )) {
      const content = document.getElementById(trigger.getAttribute("aria-controls") || "");
      const values = content?.closest(".cloud-search-filter-values-container");
      if (!(values instanceof HTMLElement) || !(trigger.parentElement instanceof HTMLElement)) {
        continue;
      }
      values.classList.add("mastoner-xcloud-global-search-values-docked");
      values.dataset.mastonerXcloudGlobalFilterPanelFor =
        trigger.dataset.mastonerXcloudGlobalFilterKey || "";
      if (values.parentElement !== trigger.parentElement) {
        trigger.insertAdjacentElement("afterend", values);
      }
    }
  }

  function xcloudFilterRoots(primaryRoot) {
    const roots = [primaryRoot];
    for (const pane of document.querySelectorAll(".cloud-search-filters-pane")) {
      if (pane instanceof Element && !roots.includes(pane)) roots.push(pane);
    }
    return roots;
  }

  function isRendered(element) {
    if (!(element instanceof HTMLElement)) return false;
    for (let current = element; current instanceof HTMLElement; current = current.parentElement) {
      if (current.hidden || current.getAttribute("aria-hidden") === "true") return false;
      const style = window.getComputedStyle(current);
      if (style.display === "none" || style.visibility === "hidden") return false;
      if (typeof current.getClientRects === "function" && current.getClientRects().length === 0) {
        return false;
      }
    }
    return true;
  }

  function detachedMobilePane() {
    for (const pane of document.querySelectorAll(".cloud-search-filters-pane")) {
      if (pane instanceof HTMLElement && isRendered(pane)) return pane;
    }
    return null;
  }

  function dialogFocusableElements(pane) {
    return [
      ...pane.querySelectorAll("a[href], button, input, select, textarea, [tabindex]"),
    ].filter(
      (element) =>
        element instanceof HTMLElement &&
        element.getAttribute("disabled") === null &&
        element.getAttribute("tabindex") !== "-1" &&
        isRendered(element),
    );
  }

  function setDefensivelyHidden(element, shouldHide) {
    if (!(element instanceof HTMLElement)) return;
    element.classList.toggle(hiddenClass, shouldHide);
    if (shouldHide) {
      element.hidden = true;
      element.setAttribute("aria-hidden", "true");
    } else if (element.dataset.mastonerXcloudGlobalSearchHidden === "true") {
      element.hidden = false;
      element.removeAttribute("aria-hidden");
    }
    element.dataset.mastonerXcloudGlobalSearchHidden = shouldHide ? "true" : "false";
  }

  function filterName(filter) {
    const named = filter.matches("[data-filter-name]")
      ? filter
      : filter.querySelector("[data-filter-name]");
    const dataName = normalize(named?.getAttribute("data-filter-name"));
    if (dataName) return canonicalFilterName(dataName) || dataName;

    let currentHeading = "";
    for (const candidate of filter.querySelectorAll(
      ".cloud-search-filter__title, .cloud-search-filter-title, .cloud-search-filter__name, .cloud-search-filter-name, summary, button",
    )) {
      const text = normalize(candidate.textContent);
      if (!text) continue;
      if (!currentHeading) currentHeading = text;
      for (const [name, translated] of labels) {
        const aliases = [name, normalize(translated)];
        if (
          aliases.some(
            (alias) =>
              text === alias || text.startsWith(`${alias} `) || text.startsWith(`${alias}:`),
          )
        ) {
          return name;
        }
      }
    }
    if (currentHeading) return currentHeading;

    const persistedName = normalize(filter.dataset.mastonerXcloudGlobalFilterKey);
    return labels.has(persistedName) ? persistedName : "";
  }

  function canonicalFilterName(value) {
    const text = normalize(value);
    return filterAliases.get(text) || "";
  }

  function replaceExactText(container, from, to) {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    for (const node of nodes) {
      if (
        node.parentElement?.closest(
          ".cloud-search-filter-value, .cloud-search-applied-filter__values",
        )
      ) {
        continue;
      }
      if (normalize(node.nodeValue) === normalize(from) && node.nodeValue !== to) {
        node.nodeValue = node.nodeValue.replace(compact(node.nodeValue), to);
      }
    }
  }

  function replaceHeadingPrefixText(container, from, to) {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const nodes = [];
    const escapedFrom = from.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");
    const prefix = new RegExp(`^(\\s*)${escapedFrom}(?=$|\\s|:)`, "iu");
    while (walker.nextNode()) nodes.push(walker.currentNode);
    for (const node of nodes) {
      if (
        node.parentElement?.closest(
          ".cloud-search-filter-value, .cloud-search-applied-filter__values",
        )
      ) {
        continue;
      }
      if (prefix.test(node.nodeValue || "")) {
        const currentValue = node.nodeValue || "";
        const nextValue = currentValue.replace(prefix, `$1${to}`);
        if (nextValue !== currentValue) node.nodeValue = nextValue;
      }
    }
  }

  function translateFilter(filter, name) {
    const translated = labels.get(name);
    if (!translated) return;
    replaceHeadingPrefixText(filter, name, translated);
    for (const element of filter.querySelectorAll("[placeholder], [aria-label], [title]")) {
      for (const attribute of ["placeholder", "aria-label", "title"]) {
        if (normalize(element.getAttribute(attribute)) === name) {
          element.setAttribute(attribute, translated);
        }
      }
    }
  }

  function translateAppliedFilters() {
    document
      .querySelectorAll(
        ".cloud-search-applied-filter__text, .cloud-search-applied-filter__name, .cloud-search-filter__name",
      )
      .forEach((element) => replaceHeadingPrefixText(element, "Collection", "Categoría"));
  }

  function collectionValue(row) {
    const visibleName = row.querySelector(
      ".cloud-search-filter-value__name, [data-filter-value-name]",
    );
    const input = row.querySelector("input[value]");
    const link = row.querySelector("a[href]");
    const candidates = [
      row.getAttribute("data-filter-value"),
      visibleName?.textContent,
      input?.getAttribute("value"),
      link?.getAttribute("data-filter-value"),
    ]
      .map(normalizeCollectionValue)
      .filter(Boolean);
    if (candidates.length === 0) return "";
    const first = candidates[0];
    return candidates.every((value) => value === first) ? first : "";
  }

  function normalizeCollectionValue(value) {
    return normalize(value).replace(/\s+\(\d+(?:[.,]\d+)?\)$/u, "");
  }

  function constrainCollection(filter) {
    const rows = filter.querySelectorAll(
      ".cloud-search-filter-value, [data-filter-value]:not(.cloud-search-filter-values-container)",
    );
    for (const row of rows) {
      const allowed = Boolean(collectionValue(row));
      row.classList.toggle(allowedValueClass, allowed);
      setDefensivelyHidden(row, !allowed);
    }
  }

  function filterAllowed(name, filterAllowlist, availableNames, contextual) {
    if (!name || contextualFilterDenylist.has(normalize(name))) return false;
    if (filterAllowlist.has(name)) return true;
    if (!contextual) return false;
    if (name === "marca" && availableNames.has("fabricante / marca impresora")) return false;
    return true;
  }

  function configureFilters(root, filterAllowlist) {
    const authority = window.__mastonerStorefrontFilterContract20260827;
    if (authority?.applySearchPolicy) {
      authority.applySearchPolicy(root);
      for (const filter of root.querySelectorAll('.cloud-search-filter[data-filter-name="Collection"]')) {
        if (!filter.hidden) constrainCollection(filter);
      }
      return;
    }
    const filters = root.querySelectorAll(".cloud-search-filter");
    const availableNames = new Set(
      [...filters].map((filter) => normalize(filterName(filter))).filter(Boolean),
    );
    const contextual = contextualFiltersEnabled();
    for (const filter of filters) {
      const name = normalize(filterName(filter));
      const allowed = filterAllowed(name, filterAllowlist, availableNames, contextual);
      filter.classList.toggle(allowedClass, allowed);
      setDefensivelyHidden(filter, !allowed);
      if (!allowed) {
        delete filter.dataset.mastonerXcloudGlobalFilterKey;
        continue;
      }
      filter.dataset.mastonerXcloudGlobalFilterKey = name;
      translateFilter(filter, name);
      if (name === "collection") constrainCollection(filter);
    }
    reorderAllowedFilters(filters);
    const relevance = root.querySelector(
      'select.cloud-search-sort-select option[value="relevance"]',
    );
    if (relevance && compact(relevance.textContent) !== "Relevancia") {
      relevance.textContent = "Relevancia";
    }
  }

  function reorderAllowedFilters(filters) {
    const parents = new Set();
    for (const filter of filters) {
      if (filter.classList.contains(allowedClass) && filter.parentElement) {
        parents.add(filter.parentElement);
      }
    }
    for (const parent of parents) {
      const current = [...parent.children].filter(
        (child) =>
          child instanceof HTMLElement &&
          child.classList.contains("cloud-search-filter") &&
          child.classList.contains(allowedClass),
      );
      const desired = [...current].sort((left, right) => {
        const leftName = normalize(filterName(left));
        const rightName = normalize(filterName(right));
        const leftRank =
          (filterOrder.get(leftName) ?? 90) +
          (left.classList.contains("cloud-search-filter-values-container") ? 1 : 0);
        const rightRank =
          (filterOrder.get(rightName) ?? 90) +
          (right.classList.contains("cloud-search-filter-values-container") ? 1 : 0);
        return leftRank - rightRank;
      });
      if (!current.some((filter, index) => filter !== desired[index])) continue;
      const boundary = current[current.length - 1]?.nextSibling || null;
      for (const filter of desired) parent.insertBefore(filter, boundary);
    }
  }

  function filterPanel(root) {
    return root.querySelector(
      ".cloud-search-page-controls__filters, .cloud-search-filters, [data-search-filters-panel]",
    );
  }

  function panelIsOpen(panel) {
    if (panel.hidden || panel.getAttribute("aria-hidden") === "true") return false;
    if (
      panel.classList.contains("is-open") ||
      panel.classList.contains("open") ||
      panel.classList.contains("mastoner-xcloud-mobile-filters-open")
    ) {
      return true;
    }
    return window.getComputedStyle(panel).display !== "none";
  }

  function setOwnedDialogSemantics(panel, shouldOwn) {
    if (shouldOwn) {
      if (panel.getAttribute("role") === null) {
        panel.setAttribute("role", "dialog");
        panel.dataset.mastonerXcloudGlobalSearchDialogRoleOwned = "true";
      }
      if (panel.getAttribute("aria-label") === null) {
        panel.setAttribute("aria-label", "Filtros");
        panel.dataset.mastonerXcloudGlobalSearchDialogLabelOwned = "true";
      }
      return;
    }
    if (panel.dataset.mastonerXcloudGlobalSearchDialogRoleOwned === "true") {
      if (panel.getAttribute("role") === "dialog") panel.removeAttribute("role");
      delete panel.dataset.mastonerXcloudGlobalSearchDialogRoleOwned;
    }
    if (panel.dataset.mastonerXcloudGlobalSearchDialogLabelOwned === "true") {
      if (panel.getAttribute("aria-label") === "Filtros") panel.removeAttribute("aria-label");
      delete panel.dataset.mastonerXcloudGlobalSearchDialogLabelOwned;
    }
  }

  function configureDetachedMobilePane(pane, trigger) {
    if (!pane.id) pane.id = "mastoner-xcloud-global-search-mobile-filters";
    setOwnedDialogSemantics(pane, true);
    if (pane.getAttribute("aria-modal") === null) {
      pane.setAttribute("aria-modal", "true");
    }
    trigger.setAttribute("aria-controls", pane.id);
    trigger.setAttribute("aria-expanded", "true");

    const close = pane.querySelector(".cloud-search-filters-pane__close");
    if (!(close instanceof HTMLElement)) return;
    close.setAttribute("role", "button");
    close.setAttribute("tabindex", "0");
    close.setAttribute("aria-label", "Cerrar filtros");
    if (close.dataset.mastonerXcloudGlobalSearchBound !== "true") {
      close.dataset.mastonerXcloudGlobalSearchBound = "true";
      close.addEventListener("click", () => {
        trigger.setAttribute("aria-expanded", "false");
        window.setTimeout(() => trigger.focus(), 0);
      });
      close.addEventListener("keydown", (event) => {
        if (!["Enter", " "].includes(event.key)) return;
        event.preventDefault();
        close.click();
      });
    }
    if (pane.dataset.mastonerXcloudGlobalSearchFocusTrapBound !== "true") {
      pane.dataset.mastonerXcloudGlobalSearchFocusTrapBound = "true";
      pane.addEventListener("keydown", (event) => {
        if (event.key !== "Tab") return;
        const focusable = dialogFocusableElements(pane);
        const first = focusable[0] || close;
        const last = focusable[focusable.length - 1] || close;
        const active = document.activeElement;
        if (event.shiftKey && (active === first || !pane.contains(active))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (active === last || !pane.contains(active))) {
          event.preventDefault();
          first.focus();
        }
      });
    }
    if (pane.dataset.mastonerXcloudGlobalSearchInitialFocus !== "true") {
      pane.dataset.mastonerXcloudGlobalSearchInitialFocus = "true";
      window.setTimeout(() => {
        if (
          mobileFiltersMedia.matches &&
          detachedMobilePane() === pane &&
          !pane.contains(document.activeElement)
        ) {
          close.focus();
        }
      }, 0);
    }
  }

  function syncMobileControls(root) {
    const panel = filterPanel(root);
    const triggers = [
      ...root.querySelectorAll(
        ".cloud-search-page-controls__filters-link, [data-search-filters-toggle]",
      ),
    ];
    if (!(panel instanceof HTMLElement) || triggers.length === 0) return;

    const trigger = triggers[0];
    for (const duplicate of triggers.slice(1)) setDefensivelyHidden(duplicate, true);
    setDefensivelyHidden(trigger, false);
    activeTrigger = trigger;

    if (!panel.id) panel.id = "mastoner-xcloud-global-search-filters";
    trigger.setAttribute("aria-controls", panel.id);
    trigger.setAttribute("aria-haspopup", "dialog");
    trigger.setAttribute("aria-label", "Filtros");
    const isFocusableLink = trigger.tagName === "A" && compact(trigger.getAttribute("href")) !== "";
    if (trigger.tagName !== "BUTTON") {
      trigger.setAttribute("role", "button");
      if (!isFocusableLink && trigger.getAttribute("tabindex") === null) {
        trigger.setAttribute("tabindex", "0");
      }
    }
    const mobilePane = mobileFiltersMedia.matches ? detachedMobilePane() : null;
    trigger.setAttribute(
      "aria-expanded",
      mobileFiltersMedia.matches && (mobilePane || panelIsOpen(panel)) ? "true" : "false",
    );
    replaceExactText(trigger, "Filters", "Filtros");
    replaceExactText(trigger, "Filter", "Filtros");

    setOwnedDialogSemantics(panel, mobileFiltersMedia.matches && mobilePane === null);
    if (mobilePane) configureDetachedMobilePane(mobilePane, trigger);

    if (trigger.dataset.mastonerXcloudGlobalSearchBound !== "true") {
      trigger.dataset.mastonerXcloudGlobalSearchBound = "true";
      trigger.addEventListener("click", () => {
        activeTrigger = trigger;
        window.setTimeout(() => schedule(), 0);
      });
      trigger.addEventListener("keydown", (event) => {
        if (trigger.tagName === "BUTTON" || !["Enter", " "].includes(event.key)) return;
        event.preventDefault();
        trigger.click();
      });
    }
  }

  function enhance() {
    scheduled = false;
    if (window.location.pathname !== pathname) return;
    const root = xcloudRoot();
    if (!root) return;
    installStyle();
    if (!ensureSearchLayout(root)) {
      delete document.documentElement.dataset.mastonerXcloudGlobalSearch;
      return;
    }
    configureFilterPresentation(root);
    dockSortInToolbar(root);
    for (const filterRoot of xcloudFilterRoots(root)) {
      configureFilters(
        filterRoot,
        filterRoot === root ? primaryFilterAllowlist : mobileFilterAllowlist,
      );
      for (const label of filterRoot.querySelectorAll(
        '.cloud-search-filter[data-filter-name="Availability"] .cloud-search-filter-value__name',
      )) {
        if (normalize(label.textContent) === "in stock") label.textContent = "Disponible";
      }
    }
    translateAppliedFilters();
    dockDesktopFilterPanels(root);
    syncMobileControls(root);
    document.documentElement.dataset.mastonerXcloudGlobalSearch = "ready";
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(enhance);
  }

  function installStyle() {
    if (document.getElementById("mastoner-xcloud-global-search-style")) return;
    const style = document.createElement("style");
    style.id = "mastoner-xcloud-global-search-style";
    style.textContent = `
      .${hiddenClass} { display: none !important; }
      #cloud_search_filters_root .cloud-search-filter:not(.${allowedClass}),
      .cloud-search-page-controls .cloud-search-filter:not(.${allowedClass}),
      .cloud-search-filters-pane .cloud-search-filter:not(.${allowedClass}) {
        display: none !important;
      }
      [data-mastoner-xcloud-global-filter-key="collection"]
        .cloud-search-filter-value:not(.${allowedValueClass}),
      [data-mastoner-xcloud-global-filter-key="collection"]
        [data-filter-value]:not(.cloud-search-filter-values-container):not(.${allowedValueClass}) {
        display: none !important;
      }
      #cloud_search_filters_root {
        background: #fff;
      }
      #shopify-section-mastoner-collection-helper.${hiddenClass} {
        display: none !important;
      }
      .mastoner-xcloud-global-search-layout {
        margin-inline: auto;
        max-width: none;
        width: min(1640px, calc(100% - 48px));
      }
      html[data-mastoner-xcloud-global-search="ready"]
        #shopify-section-main.cc-main-search {
        margin-top: 0 !important;
        min-width: 0;
      }
      html[data-mastoner-xcloud-global-search="ready"]
        #shopify-section-main.cc-main-search
        > .container {
        max-width: none !important;
        padding-inline: 0 !important;
        width: 100% !important;
      }
      html[data-mastoner-xcloud-global-search="ready"]
        #shopify-section-main.cc-main-search
        > .container
        > div.text-center:first-child {
        height: 1px;
        margin: 0 !important;
        overflow: hidden;
        position: relative;
      }
      html[data-mastoner-xcloud-global-search="ready"]
        #shopify-section-main.cc-main-search
        > .container
        > div.text-center:first-child
        > h1 {
        clip: rect(0 0 0 0);
        clip-path: inset(50%);
        height: 1px;
        overflow: hidden;
        position: absolute;
        white-space: nowrap;
        width: 1px;
      }
      html[data-mastoner-xcloud-global-search="ready"]
        #shopify-section-main.cc-main-search
        > .container
        > div.text-center:first-child
        > .form-width {
        display: none !important;
      }
      html[data-mastoner-xcloud-global-search="ready"]
        #shopify-section-main.cc-main-search
        #products-toolbar {
        margin-top: 0 !important;
      }
      html[data-mastoner-xcloud-global-search="ready"]
        #shopify-section-main.cc-main-search
        #products-toolbar
        .products-toolbar__sort {
        display: block !important;
        min-width: 0;
      }
      @media (min-width: 737px) {
        .mastoner-xcloud-global-search-layout {
          align-items: start;
          display: grid;
          gap: 48px;
          grid-template-columns: 240px minmax(0, 1fr);
          padding-top: 28px;
        }
        .mastoner-xcloud-global-search-layout
          > #shopify-section-main.cc-main-search {
          min-width: 0;
        }
        .mastoner-xcloud-global-search-layout
          > #cloud_search_filters_root {
          border: 0;
          border-radius: 0;
          max-width: 240px;
          overflow: visible;
          position: sticky;
          top: 18px;
          width: 240px;
        }
        #cloud_search_filters_root
          [data-mastoner-xcloud-global-search-presentation="sidebar"] {
          align-items: stretch;
          box-sizing: border-box;
          display: flex !important;
          flex-direction: column;
          margin: 0 !important;
          min-height: 0;
          min-width: 0;
          padding: 0 !important;
          width: 100%;
        }
        [data-mastoner-xcloud-global-search-presentation="sidebar"]::before {
          border-bottom: 1px solid #e5e7eb;
          color: #111827;
          content: "Filtros";
          font-size: 20px;
          font-weight: 750;
          line-height: 1.25;
          margin-bottom: 0;
          padding: 0 0 16px;
        }
        [data-mastoner-xcloud-global-search-presentation="sidebar"]
          .cloud-search-page-controls__totals {
          display: block !important;
        }
        #cloud_search_filters_root
          [data-mastoner-xcloud-global-search-presentation="sidebar"]
          .cloud-search-page-controls__totals {
          color: #4b5563;
          border-bottom: 1px solid #e5e7eb;
          font-size: 13px;
          font-weight: 500;
          line-height: 1.45;
          margin: 0 0 6px !important;
          min-width: 0;
          order: 3;
          padding: 16px 0 0;
        }
        #cloud_search_filters_root
          [data-mastoner-xcloud-global-search-presentation="sidebar"]
          .cloud-search-page-controls__filters-container {
          align-items: stretch;
          box-sizing: border-box;
          display: flex !important;
          flex-direction: column;
          max-width: 100%;
          min-width: 0;
          width: 100%;
        }
        #cloud_search_filters_root
          [data-mastoner-xcloud-global-search-presentation="sidebar"]
          .cloud-search-page-controls__filters-link {
          display: none !important;
        }
        #cloud_search_filters_root
          [data-mastoner-xcloud-global-search-presentation="sidebar"]
          .cloud-search-page-controls__sort {
          align-items: center;
          border-top: 1px solid #e5e7eb;
          box-sizing: border-box;
          display: flex !important;
          gap: 8px;
          margin: 10px 0 0 !important;
          max-width: 100%;
          min-width: 0;
          padding-top: 16px;
          width: 100%;
        }
        #cloud_search_filters_root
          [data-mastoner-xcloud-global-search-presentation="sidebar"]
          .cloud-search-sort-label {
          color: #4b5563;
          font-size: 14px;
          font-weight: 600;
        }
        #cloud_search_filters_root
          [data-mastoner-xcloud-global-search-presentation="sidebar"]
          .cloud-search-sort-select {
          background-color: #fff;
          border: 1px solid #d1d5db;
          border-radius: 10px;
          box-sizing: border-box;
          color: #111827;
          font: inherit;
          font-size: 15px;
          font-weight: 600;
          min-height: 42px;
          min-width: 0;
          padding: 0 34px 0 12px;
          width: 100%;
        }
        #cloud_search_filters_root
          [data-mastoner-xcloud-global-search-presentation="sidebar"]
          .cloud-search-sort-select:focus-visible {
          border-color: #006f62;
          outline: 2px solid #006f62;
          outline-offset: 2px;
        }
        #cloud_search_filters_root
          [data-mastoner-xcloud-global-search-presentation="sidebar"]
          .cloud-search-page-controls__filters {
          align-items: stretch;
          box-sizing: border-box;
          display: flex !important;
          flex-direction: column;
          max-width: 100%;
          min-width: 0;
          width: 100%;
        }
        #cloud_search_filters_root
          [data-mastoner-xcloud-global-search-presentation="sidebar"]
          .cloud-search-filter.${allowedClass}:not(.mastoner-storefront-filter-hidden) {
          align-items: center;
          background: #fff !important;
          border: 0 !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-radius: 0 !important;
          box-shadow: none;
          color: #111827;
          display: flex !important;
          font-size: 15px;
          font-weight: 700;
          justify-content: space-between;
          margin: 0 !important;
          max-width: 100%;
          min-height: 52px;
          min-width: 0;
          padding: 0 30px 0 0 !important;
          position: relative;
          width: 100%;
          transition:
            background-color 160ms ease,
            color 160ms ease;
        }
        #cloud_search_filters_root
          [data-mastoner-xcloud-global-search-presentation="sidebar"]
          .cloud-search-filter.${allowedClass}
          > .cloud-search-filter__name {
          background: transparent !important;
          border-radius: 0 !important;
          min-height: 0;
          padding: 0 !important;
        }
        #cloud_search_filters_root
          [data-mastoner-xcloud-global-search-presentation="sidebar"]
          .cloud-search-filter.${allowedClass}:hover {
          color: #006f62;
        }
        #cloud_search_filters_root
          [data-mastoner-xcloud-global-search-presentation="sidebar"]
          .cloud-search-filter.${allowedClass}:focus-visible {
          outline: 2px solid #006f62;
          outline-offset: 2px;
        }
        #cloud_search_filters_root
          [data-mastoner-xcloud-global-search-presentation="sidebar"]
          .cloud-search-filter.${allowedClass}[aria-expanded="true"] {
          color: #00594f;
        }
        #cloud_search_filters_root
          .mastoner-xcloud-global-search-values-docked {
          background: #fff !important;
          border: 0 !important;
          border-bottom: 1px solid #e5e7eb !important;
          box-shadow: none !important;
          box-sizing: border-box;
          display: block;
          inset: auto !important;
          margin: 0 !important;
          max-height: 280px;
          overflow: auto;
          padding: 4px 0 12px !important;
          position: static !important;
          transform: none !important;
          width: 100% !important;
        }
        #cloud_search_filters_root
          .mastoner-xcloud-global-search-values-docked
          .cloud-search-filter__content {
          box-sizing: border-box;
          max-height: none !important;
          padding: 0 !important;
          width: 100% !important;
        }
        #cloud_search_filters_root
          .mastoner-xcloud-global-search-values-docked
          .cloud-search-filter-value {
          box-sizing: border-box;
          line-height: 1.35;
          padding: 9px 2px !important;
          width: 100%;
        }
        #cloud_search_filters_root
          :where(.cloud-search-page-controls__applied-filters, .cloud-search-applied-filters) {
          box-sizing: border-box;
          margin-inline: 0 !important;
          max-width: 100%;
          min-width: 0;
          width: 100%;
        }
        #cloud_search_filters_root .cloud-search-applied-filter {
          box-sizing: border-box;
          margin-inline: 0 !important;
          max-width: 100%;
          min-width: 0;
          white-space: normal !important;
        }
        #cloud_search_filters_root
          :where(
            .cloud-search-applied-filter__text,
            .cloud-search-applied-filter__name,
            .cloud-search-applied-filter__values
          ) {
          min-width: 0;
          overflow-wrap: anywhere;
          white-space: normal !important;
          word-break: break-word;
        }
        #shopify-section-main.cc-main-search
          #products-toolbar
          .cloud-search-page-controls__sort[data-mastoner-xcloud-global-search-toolbar-sort="true"] {
          align-items: center;
          border: 0;
          display: flex !important;
          gap: 8px;
          margin: 0 !important;
          min-width: 0;
          padding: 0;
          width: auto;
        }
        #shopify-section-main.cc-main-search
          #products-toolbar
          .cloud-search-page-controls__sort[data-mastoner-xcloud-global-search-toolbar-sort="true"]
          .cloud-search-sort-label {
          color: inherit;
          font-size: 16px;
          font-weight: 700;
          white-space: nowrap;
        }
        #shopify-section-main.cc-main-search
          #products-toolbar
          .cloud-search-page-controls__sort[data-mastoner-xcloud-global-search-toolbar-sort="true"]
          .cloud-search-sort-select {
          background-color: #fff;
          border: 0;
          color: inherit;
          font: inherit;
          font-size: 16px;
          min-height: 42px;
          padding: 0 30px 0 8px;
        }
      }
      @media (min-width: 737px) and (max-width: 1120px) {
        .mastoner-xcloud-global-search-layout {
          gap: 24px;
          grid-template-columns: 236px minmax(0, 1fr);
        }
      }
      @media (max-width: 736px) {

        html[data-mastoner-xcloud-global-search="ready"]
          #shopify-section-main.cc-main-search
          #products-toolbar
          .products-toolbar__sort {
          display: none !important;
        }

        .cloud-search-filters-pane {
          box-sizing: border-box !important;
          padding-top: 64px !important;
        }
        .cloud-search-filters-pane__close {
          box-sizing: border-box !important;
          min-height: 48px !important;
        }
        .mastoner-xcloud-global-search-layout {
          display: block;
          padding-top: 12px;
          width: 100%;
        }
        #cloud_search_filters_root {
          border-bottom: 1px solid #e5e7eb;
          border-top: 1px solid #e5e7eb;
          padding: 12px 16px;
        }
        #cloud_search_filters_root .cloud-search-page-controls {
          display: block !important;
          margin: 0 !important;
          min-height: 0;
          padding: 0 !important;
          width: 100%;
        }
        #cloud_search_filters_root .cloud-search-page-controls__totals {
          color: #4b5563;
          display: block !important;
          font-size: 13px;
          font-weight: 500;
          line-height: 1.45;
          margin: 0 0 10px !important;
        }
        #cloud_search_filters_root .cloud-search-page-controls__filters-container {
          align-items: center;
          display: grid !important;
          gap: 8px;
          grid-template-columns: minmax(0, 1fr) auto;
          width: 100%;
        }
        #cloud_search_filters_root .cloud-search-page-controls__sort {
          align-items: center;
          display: flex !important;
          gap: 6px;
          order: 2;
          white-space: nowrap;
        }
        #cloud_search_filters_root .cloud-search-page-controls__filters-link {
          order: 1;
        }
        #cloud_search_filters_root .cloud-search-page-controls__applied-filters {
          display: flex !important;
          flex-wrap: wrap;
          gap: 8px;
          grid-column: 1 / -1;
          min-width: 0;
          order: 3;
          width: 100%;
        }
        #cloud_search_filters_root .cloud-search-page-controls__applied-filters
          .cloud-search-applied-filter {
          box-sizing: border-box;
          margin: 0;
          max-width: 100%;
          width: auto;
        }
        #cloud_search_filters_root .cloud-search-sort-label {
          clip: rect(0 0 0 0);
          clip-path: inset(50%);
          height: 1px;
          overflow: hidden;
          position: absolute;
          white-space: nowrap;
          width: 1px;
        }
        #cloud_search_filters_root .cloud-search-sort-select {
          background-color: #fff;
          border: 1px solid #d1d5db;
          border-radius: 10px;
          box-sizing: border-box;
          color: #111827;
          font: inherit;
          font-size: 14px;
          font-weight: 600;
          min-height: 48px;
          max-width: 148px;
          padding: 0 30px 0 10px;
        }
        #cloud_search_filters_root .cloud-search-sort-select:focus-visible {
          border-color: #006f62;
          outline: 2px solid #006f62;
          outline-offset: 2px;
        }
        [data-mastoner-xcloud-global-search-bound="true"] {
          align-items: center;
          background: #fff;
          border: 1px solid #d1d5db;
          border-radius: 10px;
          box-sizing: border-box;
          color: #111827;
          display: flex !important;
          font-size: 15px;
          font-weight: 700;
          justify-content: flex-start;
          min-height: 48px;
          min-width: 44px;
          padding: 0 14px;
          width: 100%;
        }
        [data-mastoner-xcloud-global-search-bound="true"]:focus-visible {
          outline: 2px solid #006f62;
          outline-offset: 2px;
        }
        [data-mastoner-xcloud-global-search-bound="true"]
          .cloud-search-filters-link-icon {
          flex: 0 0 16px;
          margin-right: 8px;
          position: static !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  document.addEventListener(
    "keydown",
    (event) => {
      if (event.key !== "Escape" || !mobileFiltersMedia.matches) return;
      const mobilePane = detachedMobilePane();
      if (mobilePane) {
        const close = mobilePane.querySelector(".cloud-search-filters-pane__close");
        if (!(close instanceof HTMLElement)) return;
        event.preventDefault();
        event.stopPropagation();
        activeTrigger?.setAttribute("aria-expanded", "false");
        close.click();
        window.setTimeout(() => activeTrigger?.focus(), 0);
        return;
      }
      if (activeTrigger?.getAttribute("aria-expanded") !== "true") return;
      event.preventDefault();
      activeTrigger.click();
      window.setTimeout(() => activeTrigger?.focus(), 0);
    },
    true,
  );

  window[API_NAME] = {
    selectedCollectionValues,
    contextualFiltersEnabled,
    filterAllowed,
    enhance,
    normalize,
  };

  const observer = new MutationObserver((records) => {
    const identityChanged = records.some(
      ({ type }) => type === "attributes" || type === "characterData",
    );
    if (identityChanged) {
      enhance();
      return;
    }
    schedule();
  });
  if (typeof mobileFiltersMedia.addEventListener === "function") {
    mobileFiltersMedia.addEventListener("change", schedule);
  } else {
    mobileFiltersMedia.addListener(schedule);
  }
  const observe = () => {
    observer.observe(document.body, {
      attributeFilter: ["data-filter-name", "data-filter-value", "value"],
      attributes: true,
      characterData: true,
      childList: true,
      subtree: true,
    });
    schedule();
  };
  if (document.body) observe();
  else document.addEventListener("DOMContentLoaded", observe, { once: true });
})();
/* MASTONER_XCLOUD_GLOBAL_SEARCH_20260825_END */
